import {
  REVIEWER_LANES,
  REVIEWER_LANE_GAP,
  ReviewSourceNodeData,
  ReviewerEdge,
  ReviewerLaneId,
  ReviewerNode,
} from './graphTypes';

/** Which fork of the verdict a policy hangs off, top to bottom. */
const VERDICT_RANK: Record<string, number> = {
  review_pass: 0,
  review_uncertain: 1,
  review_fail: 2,
};

const laneOf = (node: ReviewerNode): ReviewerLaneId => {
  if (node.type === 'review-source') {
    return (node.data as ReviewSourceNodeData).lane === 'image' ? 'sourceImage' : 'sourceText';
  }
  if (node.type === 'reviewer-core') return 'core';
  if (node.type === 'review-verdict') return 'verdict';
  return 'policy';
};

/**
 * Fixed lanes, left to right: what the judge reads (text row above, image row
 * below) → the judge → its verdict → what it may then do.
 *
 * The layout is DERIVED, never stored and never draggable: the reviewer graph
 * is a picture of a configuration, not a diagram someone owns, so the same
 * profile must always draw the same shape. Positions are integers and depend
 * on nothing but the nodes and edges handed in.
 *
 * `edges` are read for one thing only — which fork of the verdict feeds each
 * policy, so the policy column stacks pass, then uncertain, then fail, and the
 * three flows never cross.
 */
export function reviewerLayout(nodes: ReviewerNode[], edges: ReviewerEdge[] = []): ReviewerNode[] {
  const textRows = nodes.filter((node) => laneOf(node) === 'sourceText');
  const imageRows = nodes.filter((node) => laneOf(node) === 'sourceImage');
  const policies = nodes.filter((node) => laneOf(node) === 'policy');

  const textPitch = REVIEWER_LANES.sourceText.pitch;
  const imagePitch = REVIEWER_LANES.sourceImage.pitch;
  const imageTop = textRows.length ? textRows.length * textPitch + REVIEWER_LANE_GAP : 0;

  const y = new Map<string, number>();
  textRows.forEach((node, index) => y.set(node.id, index * textPitch));
  imageRows.forEach((node, index) => y.set(node.id, imageTop + index * imagePitch));

  const sourceBottom = imageRows.length
    ? imageTop + (imageRows.length - 1) * imagePitch
    : Math.max(0, (textRows.length - 1) * textPitch);
  const centre = Math.round(sourceBottom / 2);

  // The policy column stacks by verdict fork; within a fork the profile's own
  // order stands (the backend emits policies in a fixed, meaningful order).
  const rankOf = (nodeId: string): number => {
    const incoming = edges.find((edge) => edge.target === nodeId);
    return VERDICT_RANK[String(incoming?.data?.kind || '')] ?? VERDICT_RANK.review_fail;
  };
  const policyPitch = REVIEWER_LANES.policy.pitch;
  const policyTop = centre - Math.round(((policies.length - 1) * policyPitch) / 2);
  policies
    .map((node, index) => ({ node, index, rank: rankOf(node.id) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .forEach((row, position) => y.set(row.node.id, policyTop + position * policyPitch));

  return nodes.map((node) => {
    const lane = laneOf(node);
    return {
      ...node,
      position: { x: REVIEWER_LANES[lane].x, y: y.get(node.id) ?? centre },
    };
  });
}

export default reviewerLayout;
